import { isStrictEqual, randomString } from 'remeda';
import { BaseSelectItem } from 'wbs-components-lib';
import { string } from 'yup';

import { YUP_ERRORS_MESSAGES } from '@shared/constants';
import {
  RewardModel,
  RewardModelWithChance,
  RewardModelWithCount,
  RewardModelWithoutId,
  RewardTypeFieldNamesEnum,
} from '@shared/types';
import { getMapValues, validateObjectId } from '@shared/utils';

import { UserRewardType } from 'v2/constants';

export const getRewardTypeSelectItems = (excludedRewardTypes: UserRewardType[] = []) => {
  const selectItems: BaseSelectItem[] = Object.values(UserRewardType)
    .filter((rewardType) => !excludedRewardTypes.includes(rewardType))
    .map((rewardType) => ({
      id: rewardType,
      value: rewardType,
    }));

  return selectItems;
};

export const getRewardItem = (): RewardModel => ({
  id: randomString(5),
  type: UserRewardType.BALANCE,
  value: '',
});

export const getRewardModelsWithoutIdAndCountFromRewardItems = (
  rewardItems: RewardModelWithCount[]
): RewardModelWithoutId[] =>
  rewardItems.map((rewardItem) => {
    const { id, count, ...mainRewardInfo } = rewardItem;

    return mainRewardInfo;
  });

export const getMultipliedRewardsByCount = (rewards: RewardModelWithCount[]) =>
  rewards.reduce((accumulatedMultipliedRewards: RewardModelWithCount[], currentReward: RewardModelWithCount) => {
    const { count } = currentReward;
    const multipliedRewardsByCount = Array(Number(count)).fill(currentReward);

    return accumulatedMultipliedRewards.concat(multipliedRewardsByCount);
  }, []);

export const getRewardModelsWithIdFromRewardItems = (rewardItems: RewardModel[]): RewardModel[] =>
  rewardItems.map((rewardItem) => {
    const { type, value, id } = rewardItem;

    return new RewardModel(id, type, value);
  });

export const getDeletedRewardIds = (initialRewards: RewardModel[], currentRewards: RewardModel[]) => {
  return initialRewards
    .filter((reward) => {
      const { id: initialRewardId } = reward;

      return currentRewards.every((currentReward) => {
        const { id: currentRewardId } = currentReward;

        return initialRewardId !== currentRewardId;
      });
    })
    .map((deletedReward) => deletedReward.id);
};

export const getCreatedRewardItems = <T extends RewardModel, P extends RewardModel>(
  initialRewards: T[],
  currentRewards: P[]
) => {
  return currentRewards.filter((currentReward) => {
    const { id: currentRewardId } = currentReward;

    return initialRewards.every((initialReward) => {
      const { id: initialRewardId } = initialReward;

      return initialRewardId !== currentRewardId;
    });
  });
};

export const getRestRewardItems = <T extends RewardModel, P extends RewardModel>(
  initialRewards: T[],
  currentRewards: P[]
) => {
  const createdRewardItems = getCreatedRewardItems(initialRewards, currentRewards);

  return currentRewards.filter((currentReward) => {
    const { id: currentRewardId } = currentReward;

    const isRewardCreated = createdRewardItems.some((createdRewardItem) => {
      const { id: createdRewardId } = createdRewardItem;

      return createdRewardId === currentRewardId;
    });

    return !isRewardCreated;
  });
};

export const getUpdatedRewardItems = <T extends RewardModel, P extends RewardModel>(
  initialRewards: T[],
  currentRewards: P[]
) => {
  const restRewardItems = getRestRewardItems(initialRewards, currentRewards);

  return restRewardItems.filter((restRewardItem) => {
    const initialRewardItem = initialRewards.find((initialReward) => initialReward.id === restRewardItem.id);

    return !isStrictEqual(initialRewardItem!, restRewardItem as any);
  });
};

export const getChangedRewardsInfo = <T extends RewardModel, P extends RewardModel>(
  initialRewards: T[],
  currentRewards: P[]
) => {
  const deletedRewardIds = getDeletedRewardIds(initialRewards, currentRewards);

  const createdRewardItems = getCreatedRewardItems(initialRewards, currentRewards);

  const updatedRewardItems = getUpdatedRewardItems(initialRewards, currentRewards);

  const restRewardItems = getRestRewardItems(initialRewards, currentRewards);

  return {
    deletedRewardIds,
    createdRewardItems,
    updatedRewardItems,
    restRewardItems,
  };
};

export const getRewardValueValidationSchema = (
  typeFieldName: RewardTypeFieldNamesEnum = RewardTypeFieldNamesEnum.TYPE
) =>
  string()
    .when(typeFieldName, ([type], schema) => {
      if (type === UserRewardType.FREE_SPINS) {
        return schema.test({
          message: YUP_ERRORS_MESSAGES.OBJECT_ID,
          test: validateObjectId,
        });
      } else {
        return schema;
      }
    })
    .required(YUP_ERRORS_MESSAGES.REQUIRED);

export const getRewardWithCountItem = (): RewardModelWithCount => ({
  id: randomString(5),
  type: UserRewardType.BALANCE,
  value: '',
  count: 1,
});

export const getRewardWithChanceItem = (): RewardModelWithChance => ({
  id: randomString(5),
  type: UserRewardType.BALANCE,
  value: '',
  chance: 10,
});

export const getRewardsWithCount = (rewards: RewardModel[]) => {
  const rewardsWithCount = new Map<string, RewardModelWithCount>();

  rewards.forEach((currentReward) => {
    const { value, type } = currentReward;

    const rewardKey = `${type}_${value}`;

    const rewardWithCount = rewardsWithCount.get(rewardKey);

    if (rewardWithCount !== undefined) {
      const { count: currentCount = 0 } = rewardWithCount;

      rewardsWithCount.set(rewardKey, {
        ...currentReward,
        count: currentCount + 1,
      });
    } else {
      rewardsWithCount.set(rewardKey, {
        ...currentReward,
        count: 1,
      });
    }
  });

  return getMapValues(rewardsWithCount);
};
